import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "erydon_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item) => {
    // item: { product_id, name, price, color, image, quantity }
    setItems((prev) => {
      const idx = prev.findIndex(
        (p) => p.product_id === item.product_id && p.color === item.color
      );
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + item.quantity };
        return copy;
      }
      return [...prev, item];
    });
  }, []);

  const updateQuantity = useCallback((product_id, color, quantity) => {
    setItems((prev) =>
      prev
        .map((p) =>
          p.product_id === product_id && p.color === color ? { ...p, quantity } : p
        )
        .filter((p) => p.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((product_id, color) => {
    setItems((prev) => prev.filter((p) => !(p.product_id === product_id && p.color === color)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { items, addItem, updateQuantity, removeItem, clearCart, subtotal, count };
  }, [items, addItem, updateQuantity, removeItem, clearCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
